import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import MultiSelectCst from "@components/InputCst/MultiSelectCst";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import {
  ACTIVITY_TYPE_VALUES,
  FilterableField,
  isExcludedSearchField,
  SearchFilterValues,
} from "./types";

interface SearchFiltersAccordionProps {
  open: boolean;
  onClose: () => void;
  fields: FilterableField[];
  value: SearchFilterValues;
  onApply: (next: SearchFilterValues) => void;
  columns?: 2 | 3 | 4;
}

const toIsoDay = (v: any): string | null => {
  if (!v) return null;
  if (typeof v === "string") return v.slice(0, 10);
  if (typeof v?.format === "function") return v.format("YYYY-MM-DD");
  return null;
};

const SearchFiltersAccordion: React.FC<SearchFiltersAccordionProps> = ({
  open,
  onClose,
  fields,
  value,
  onApply,
  columns = 4,
}) => {
  const { t } = useTranslation("catalog-v2");
  const { t: tInput } = useTranslation("input");
  const theme = useTheme();

  const [draft, setDraft] = useState<SearchFilterValues>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const setField = (field: string, next: any) =>
    setDraft((prev) => ({ ...prev, [field]: next }));

  const fieldLabel = (field: string): string =>
    String(t(`search-filters.fields.${field}`, { defaultValue: field }));

  const buildControls = (
    fieldDef: FilterableField,
  ): { key: string; node: React.ReactNode }[] => {
    const { field, type } = fieldDef;
    const current = draft[field];

    if (field === "activityType") {
      const options = ACTIVITY_TYPE_VALUES.map((v) => ({
        value: v,
        label: String(
          tInput(`options.activity_type.${v.toLowerCase()}`, {
            defaultValue: v,
          }),
        ),
      }));
      return [
        {
          key: field,
          node: (
            <MultiSelectCst
              id={`lo-filter-${field}`}
              label={fieldLabel(field)}
              value={(current || []).map(
                (v: any) =>
                  options.find((o) => o.value === (v?.value ?? v)) ?? {
                    value: v,
                    label: String(v),
                  },
              )}
              options={options}
              onChange={(next: any) =>
                setField(
                  field,
                  (next || []).map((o: any) => o?.value ?? o),
                )
              }
            />
          ),
        },
      ];
    }

    switch (type) {
      case "TEXT":
      case "KEYWORD":
        return [
          {
            key: field,
            node: (
              <InputCst
                id={`lo-filter-${field}`}
                label={fieldLabel(field)}
                value={current ?? ""}
                onChange={(e: any) => setField(field, e.target.value)}
              />
            ),
          },
        ];
      case "INTEGER":
      case "LONG":
      case "DECIMAL":
        return [
          {
            key: `${field}-min`,
            node: (
              <InputCst
                id={`lo-filter-${field}-min`}
                type="number"
                label={`${fieldLabel(field)} (${t("search-filters.min")})`}
                value={current?.min ?? ""}
                onChange={(e: any) =>
                  setField(field, { ...current, min: e.target.value })
                }
              />
            ),
          },
          {
            key: `${field}-max`,
            node: (
              <InputCst
                id={`lo-filter-${field}-max`}
                type="number"
                label={`${fieldLabel(field)} (${t("search-filters.max")})`}
                value={current?.max ?? ""}
                onChange={(e: any) =>
                  setField(field, { ...current, max: e.target.value })
                }
              />
            ),
          },
        ];
      case "DATE":
        return [
          {
            key: `${field}-from`,
            node: (
              <DataPickerCst
                id={`lo-filter-${field}-from`}
                label={`${fieldLabel(field)} (${t("search-filters.from")})`}
                value={current?.from ?? null}
                onChange={(v: any) =>
                  setField(field, { ...current, from: toIsoDay(v) })
                }
              />
            ),
          },
          {
            key: `${field}-to`,
            node: (
              <DataPickerCst
                id={`lo-filter-${field}-to`}
                label={`${fieldLabel(field)} (${t("search-filters.to")})`}
                value={current?.to ?? null}
                onChange={(v: any) =>
                  setField(field, { ...current, to: toIsoDay(v) })
                }
              />
            ),
          },
        ];
      case "BOOLEAN":
        return [
          {
            key: field,
            node: (
              <SelectCst
                id={`lo-filter-${field}`}
                label={fieldLabel(field)}
                value={current ?? ""}
                options={[
                  { value: "", label: String(t("search-filters.bool-all")) },
                  {
                    value: "true",
                    label: String(t("search-filters.bool-yes")),
                  },
                  {
                    value: "false",
                    label: String(t("search-filters.bool-no")),
                  },
                ]}
                onChange={(next: any) => setField(field, next || undefined)}
              />
            ),
          },
        ];
      default:
        return [];
    }
  };

  const controls = fields
    .filter((f) => !isExcludedSearchField(f.field))
    .flatMap(buildControls);

  const itemMd = 12 / columns;

  return (
    <Collapse in={open} unmountOnExit>
      <Box
        sx={{
          mt: 2,
          padding: "20px 24px",
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          "& .MuiFormControl-root": {
            minWidth: "0 !important",
            width: "100% !important",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          {t("filters-modal.title")}
        </Typography>

        <Grid container spacing={2}>
          {controls.map(({ key, node }) => (
            <Grid item xs={12} sm={6} md={itemMd} key={key}>
              {node}
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setDraft({})}
            variant="text"
            sx={{
              textTransform: "none",
              color: theme.palette.primary.main,
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {t("filters-modal.reset")}
          </Button>
          <Button
            onClick={() => {
              onApply(draft);
            }}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {t("filters-modal.apply")}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
};

export default SearchFiltersAccordion;
